import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { UNIT_ECONOMICS, contactHref } from './siteFacts';

/**
 * Where the margin goes — the seed deck's $350 → $40 bridge, with the
 * visitor's own numbers.
 *
 * The defaults are the deck's illustrative ECM member-month. The one number
 * we do not supply is how much of the interface cost WeaveIn removes: the
 * visitor sets it, and the result says so. That is the same discipline the
 * employer ROI calculator follows for retention.
 */

const money = (n: number) => `${n < 0 ? '–' : ''}$${Math.abs(Math.round(n)).toLocaleString()}`;

interface FieldProps {
  label: string;
  hint?: string;
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
}

const Field: React.FC<FieldProps> = ({ label, hint, value, onChange, min = 0, max, step = 1, suffix }) => (
  <label className="block">
    <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{label}</span>
    <div className="mt-1 flex items-center gap-2">
      <input
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Math.max(min, Number(e.target.value) || 0))}
        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-gray-900 dark:text-gray-100"
      />
      {suffix && <span className="text-sm text-gray-500">{suffix}</span>}
    </div>
    {hint && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">{hint}</span>}
  </label>
);

const MarginCalculator: React.FC = () => {
  const [rate, setRate] = useState(UNIT_ECONOMICS.contractedRate);
  const [care, setCare] = useState(UNIT_ECONOMICS.careDelivery);
  const [admin, setAdmin] = useState(UNIT_ECONOMICS.interfaceAdmin);
  const [rework, setRework] = useState(UNIT_ECONOMICS.reworkDenials);
  const [never, setNever] = useState(UNIT_ECONOMICS.neverSubmitted);
  const [members, setMembers] = useState(100);
  const [removed, setRemoved] = useState(0);

  const r = useMemo(() => {
    const iface = admin + rework + never;
    const margin = rate - care;
    const net = margin - iface;
    const recovered = (iface * removed) / 100;
    return { iface, margin, net, recovered, netAfter: net + recovered, share: margin > 0 ? Math.round((iface / margin) * 100) : 0 };
  }, [rate, care, admin, rework, never, removed]);

  const rows = [
    { label: 'Contracted rate', v: rate, tone: 'bg-v2accent' },
    { label: '– Care delivery', v: -care, tone: 'bg-gray-400' },
    { label: '– Interface admin', v: -admin, tone: 'bg-v2accent' },
    { label: '– Rework & denials', v: -rework, tone: 'bg-v2accent' },
    { label: '– Never submitted', v: -never, tone: 'bg-v2accent' },
  ];
  const scale = Math.max(rate, 1);

  return (
    <section className="py-16 bg-gray-50 dark:bg-gray-900 border-y border-gray-200 dark:border-gray-800" id="margin-calculator">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl md:text-3xl font-bold text-ink mb-3">Where your margin goes</h2>
        <p className="text-gray-600 dark:text-gray-300 max-w-3xl mb-8">
          One member, one month. The defaults are an illustrative ECM member at a $350 contracted rate; rates are negotiated
          plan by plan, so put in your own.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,340px)_1fr] gap-8">
          <div className="grid grid-cols-2 lg:grid-cols-1 gap-4 bg-white dark:bg-gray-950 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
            <Field label="Contracted rate" value={rate} onChange={setRate} suffix="/ member / mo" />
            <Field label="Care delivery cost" value={care} onChange={setCare} hint="Staff time with the member" />
            <Field label="Interface admin" value={admin} onChange={setAdmin} hint="Forms, portals, member files" />
            <Field label="Rework & denials" value={rework} onChange={setRework} />
            <Field label="Never submitted" value={never} onChange={setNever} hint="Delivered but not billed" />
            <Field label="Members per month" value={members} onChange={setMembers} />
            <Field
              label="Interface cost removed"
              value={removed}
              onChange={(n) => setRemoved(Math.min(100, n))}
              max={100}
              suffix="%"
              hint="Your assumption. We do not set it for you."
            />
          </div>
          <div className="bg-white dark:bg-gray-950 rounded-2xl border border-gray-200 dark:border-gray-800 p-6">
            <ul className="space-y-3">
              {rows.map((row) => (
                <li key={row.label} className="grid grid-cols-[150px_1fr_72px] items-center gap-3 text-sm">
                  <span className="text-gray-700 dark:text-gray-200">{row.label}</span>
                  <span className="h-5 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden">
                    <span
                      className={`block h-full ${row.tone} transition-all duration-500`}
                      style={{ width: `${Math.min(100, (Math.abs(row.v) / scale) * 100)}%` }}
                    />
                  </span>
                  <span className="text-right font-semibold tabular-nums text-gray-900 dark:text-gray-100">{money(row.v)}</span>
                </li>
              ))}
              <li className="grid grid-cols-[150px_1fr_72px] items-center gap-3 pt-3 border-t border-gray-200 dark:border-gray-800">
                <span className="font-bold text-gray-900 dark:text-gray-100">Net to you</span>
                <span className="h-5 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden">
                  <span className="block h-full bg-v2accent transition-all duration-500" style={{ width: `${Math.max(0, Math.min(100, (r.net / scale) * 100))}%` }} />
                </span>
                <span className="text-right font-bold tabular-nums text-gray-900 dark:text-gray-100">{money(r.net)}</span>
              </li>
            </ul>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-xl bg-v2accent-soft p-4">
                <p className="text-2xl font-bold text-ink">{r.share}%</p>
                <p className="text-xs text-gray-600 dark:text-gray-300">of your margin taken by funder interfaces</p>
              </div>
              <div className="rounded-xl bg-v2accent-soft p-4">
                <p className="text-2xl font-bold text-ink">{money(r.iface * members * 12)}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300">interface cost a year, at {members.toLocaleString()} members</p>
              </div>
              <div className="rounded-xl bg-v2accent-soft p-4">
                <p className="text-2xl font-bold text-v2accent">{money(r.recovered * members * 12)}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  a year back, if {removed}% of it is removed{removed === 0 ? ' — set your assumption' : ''}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
              Illustrative, from the seed deck’s unit economics; delivery cost assumes a 1:30 caseload. Being validated across our
              top five accounts.
            </p>
            <Link to={contactHref('deliver', 'margin calculator')} className="mt-4 inline-flex items-center font-semibold text-ink hover:underline">
              Walk through your own funders with us
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarginCalculator;
